import React, { useEffect, useMemo, useRef, useState } from "react";
import Page from "components/page/Page";
import { useTranslation } from "react-i18next";
import {
  DeleteCampaign,
  GetEmailCampaigns,
  IEmailCampaign,
  PostCampaignDuplicate,
  PostCampaignUnpublish,
  PutScheduleEmailCampaign,
} from "api/rpc/2024-04/facilityAdmin/marketing/emailCampaigns";
import { useDispatch } from "react-redux";
import Card from "components/card/Card";
import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";
import { useHistory, useParams, useLocation } from "react-router";
import Accordion from "./Accordion";
import { formatDate, parseLocalDateString } from "helpers/Helpers";
import classNames from "classnames";
import AudienceCustomersTable from "./AudienceCustomersTable";
import { canUnpublishEmailCampaign } from "./emailCampaignDesignStatus";
import { EmailCampaignUnpublishConfirmModal } from "./EmailCampaignUnpublishConfirmModal";
import {
  EmailCampaignScheduleForm,
  isEmailCampaignScheduleCompleteFromCampaign,
} from "./EmailCampaignSchedulePanel";
import {
  buildEmailCampaignSchedulePayloadFromCampaign,
  TEmailCampaignScheduleFields,
} from "./emailCampaignScheduleState";
import EmailCampaignAnalytics from "./EmailCampaignAnalytics";
import EmailCampaignPreview from "./EmailCampaignPreview";
import EmailCampaignBadge from "./EmailCampaignBadge";
import { faChevronDown } from "@fortawesome/pro-light-svg-icons/faChevronDown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";

interface IPageState {
  campaign: IEmailCampaign;
  selectedTab: TabType;
}

enum TabType {
  Analytics = 0,
  Details,
  Audience,
  Preview,
}

function emailCampaignCreatePath(campaignId: number): string {
  return `/admin/email-marketing/campaigns/${campaignId}/new`;
}

export default function EmailCampaign() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { id: campaignId } = useParams<{ id: string }>();

  const [state, setState] = useState<IPageState>({
    campaign: {
      id: 0,
      name: "",
      type: "email",
      status: "draft",
      description: "",
      subject: "",
      text_preview: "",
      content: "",
      from_name: "",
      from_address: "",
      scheduled_type: "now",
      scheduled_at: formatDate(new Date()),
    },
    selectedTab: TabType.Details,
  });
  const [unpublishConfirmOpen, setUnpublishConfirmOpen] = useState(false);
  const [unpublishConfirming, setUnpublishConfirming] = useState(false);
  const scheduleSnapshotRef = useRef<TEmailCampaignScheduleFields | null>(null);

  async function loadCampaign() {
    const resp = await GetEmailCampaigns({ id: campaignId }, false);
    if (resp.status !== StatusCode.OK) {
      dispatch(showError(t("Could not find email campaign")));
      history.push("/admin/email-marketing/campaigns");
      return;
    }

    const loadedCampaign = resp.data.pop();
    if (loadedCampaign == null) {
      dispatch(showError(t("Could not find email campaign")));
      history.push("/admin/email-marketing/campaigns");
      return;
    }

    if (loadedCampaign.status === "draft") {
      history.replace(emailCampaignCreatePath(loadedCampaign.id));
      return;
    }

    setState(prev => ({
      ...prev,
      campaign: {
        ...prev.campaign,
        ...loadedCampaign,
      },
      selectedTab: loadedCampaign.status === "published" ? TabType.Analytics : TabType.Details,
    }));
  }

  useEffect(() => {
    void loadCampaign();
  }, [campaignId, location.key]);

  const canUnpublish = canUnpublishEmailCampaign(state.campaign.status);
  const canEditSchedule = state.campaign.published_at == null 
    && state.campaign.status !== 'pending'
    && state.campaign.status !== 'published';

  const campaignAudiences = state.campaign.audiences ?? [];

  function updateCampaignSchedule(fields: TEmailCampaignScheduleFields) {
    setState(prev => ({
      ...prev,
      campaign: {
        ...prev.campaign,
        ...fields,
      },
    }));
  }

  async function saveSchedule() {
    const payload = buildEmailCampaignSchedulePayloadFromCampaign(state.campaign.id, state.campaign);
    if (payload == null) {
      dispatch(showError(t("Choose when to send this campaign.")));
      return false;
    }

    const resp = await PutScheduleEmailCampaign(payload, true);
    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message || t("Could not save schedule settings.")));
      return false;
    }

    if (resp.data != null && typeof resp.data === "object" && "id" in resp.data) {
      setState(prev => ({
        ...prev,
        campaign: {
          ...prev.campaign,
          ...resp.data,
        },
      }));
    }

    dispatch(showSuccess(t("Schedule settings saved")));
    return true;
  }

  const tabs = [
    {
      id: TabType.Analytics,
      content: t("Overview"),
    },
    {
      id: TabType.Details,
      content: t("Details"),
    },
    {
      id: TabType.Audience,
      content: t("Smart list"),
    },
    {
      id: TabType.Preview,
      content: t("Preview email"),
    },
  ];

  const accordionItems = [
    {
      success_badge: true,
      title: t("Smart list"),
      success_preview: (
        <div className="text-sm">
          <span className="font-medium">{t('Sent to')}:</span> {campaignAudiences.map(a => a.title).join(", ") || t("No customers")}
        </div>
      ),
    },
    {
      success_badge: true,
      title: t("From"),
      success_preview: (
        <div className="text-sm">
          <span className="font-medium">{t('Name')}:</span> {state.campaign.from_name} • <span className="font-medium">{t('Email')}:</span> {state.campaign.from_address} {"@teeon.com"}
        </div>
      ),
    },
    {
      success_badge: true,
      title: t("Subject and preview text"),
      success_preview: (
        <div className="text-sm">
          <span className="font-medium">{t("Subject line")}:</span> {state.campaign.subject} •{" "}
          <span className="font-medium">{t("Preview text")}:</span> {state.campaign.text_preview}
        </div>
      ),
    },
    {
      success_badge: isEmailCampaignScheduleCompleteFromCampaign(state.campaign),
      title: t("Schedule time"),
      expandable: canEditSchedule,
      success_preview: (
        <div className="text-sm">
          <span className="font-medium">{t('For')}</span>: {moment(state.campaign.scheduled_at).format('LL [•] LT')}
        </div>
      ),
      onOpen: () => {
        scheduleSnapshotRef.current = {
          scheduled_type: state.campaign.scheduled_type,
          scheduled_at: state.campaign.scheduled_at,
          scheduled_recurring_type: state.campaign.scheduled_recurring_type,
        };
      },
      onCloseWithoutSave: () => {
        if (scheduleSnapshotRef.current != null) {
          updateCampaignSchedule(scheduleSnapshotRef.current);
        }
      },
      onSave: async () => saveSchedule(),
      content: (
        <EmailCampaignScheduleForm
          value={{
            scheduled_type: state.campaign.scheduled_type,
            scheduled_at: state.campaign.scheduled_at,
            scheduled_recurring_type: state.campaign.scheduled_recurring_type,
          }}
          onChange={updateCampaignSchedule}
        />
      ),
    },
  ];

  async function duplicate() {
    const resp = await PostCampaignDuplicate({ id: state.campaign.id });

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return;
    }

    const duplicatedCampaign = resp.data?.data;
    if (duplicatedCampaign == null || typeof duplicatedCampaign.id !== "number") {
      dispatch(showError(t("Could not open duplicated campaign.")));
      return;
    }

    dispatch(showSuccess(t("Duplicated email campaign")));
    history.push(emailCampaignCreatePath(duplicatedCampaign.id));
  }

  async function confirmUnpublish() {
    if (unpublishConfirming || state.campaign.id < 1) {
      return;
    }

    setUnpublishConfirming(true);

    try {
      const resp = await PostCampaignUnpublish({ id: state.campaign.id }, true);

      if (resp.status !== StatusCode.OK) {
        dispatch(showError(resp.message));
        return;
      }

      dispatch(showSuccess(resp.message || t("Campaign unpublished")));
      setUnpublishConfirmOpen(false);
      history.push(emailCampaignCreatePath(state.campaign.id) + '?step=4');
    } finally {
      setUnpublishConfirming(false);
    }
  }

  async function deleteCampaign() {
    const resp = await DeleteCampaign({ id: state.campaign.id });

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return;
    }

    dispatch(showSuccess(t("Deleted email campaign")));
    history.push("/admin/email-marketing");
  }

  const campaignTimestamp = useMemo(
    () =>
      formatDate(
        parseLocalDateString(
          state.campaign.published_at || state.campaign.scheduled_at || state.campaign.created_at,
        ) ?? new Date(),
      ),
    [state.campaign],
  );

  return (
    <Page
      title={state.campaign.name}
      titleMetadata={<EmailCampaignBadge campaign={state.campaign} size="medium" />}
      subtitle={
        <div className="text-sm text-gray-700">
          {[state.campaign.description, campaignTimestamp].filter(Boolean).join(" • ")}
        </div>
      }
      primaryAction={
        canUnpublish
          ? {
            content: t("Unpublish"),
            action: () => setUnpublishConfirmOpen(true),
          }
          : undefined
      }
      multipleActionDropdownAction={{
        label: t("Quick Actions "),
        icon: <FontAwesomeIcon className="ml-2" size="sm" icon={faChevronDown} />,
        dropdownProps: {
          alignment: "right",
          options: [
            ...(canUnpublish
              ? [
                {
                  label: t("Unpublish"),
                  icon: "eye-slash" as const,
                  type: "handler" as const,
                  handler: () => setUnpublishConfirmOpen(true),
                },
              ]
              : []),
            {
              label: t("Duplicate"),
              icon: "copy",
              type: "handler",
              handler: duplicate,
            },
            {
              label: t("Delete"),
              icon: "trash",
              type: "handler",
              handler: deleteCampaign,
            },
          ],
        },
      }}
      breadcrumbs={[
        {
          label: t("Email Marketing"),
          url: "/admin/email-marketing",
        },
        {
          label: state.campaign.name,
        },
      ]}
    >
      <div className="flex border-b border-gray-200 mb-4 *:px-4">
        {tabs.map(tab => (
          <div
            key={tab.id}
            className={classNames("py-2 text-center cursor-pointer", {
              "border-b-2": tab.id === state.selectedTab,
            })}
            onClick={() => setState(prev => ({ ...prev, selectedTab: tab.id }))}
          >
            {tab.content}
          </div>
        ))}
      </div>

      {state.selectedTab === TabType.Analytics && (
        <EmailCampaignAnalytics campaignId={state.campaign.id} />
      )}

      {state.selectedTab === TabType.Details && <Accordion items={accordionItems} />}

      {state.selectedTab === TabType.Audience && (
        <Card>
          <Card.Section>
            <div className="space-y-2">
              {campaignAudiences.map(a => (
                <AudienceCustomersTable key={a.id} audience={a} showAudienceTitle={true} />
              ))}
            </div>
          </Card.Section>
        </Card>
      )}

      {state.selectedTab === TabType.Preview && (
        <EmailCampaignPreview
          campaignId={state.campaign.id}
          design={state.campaign.design}
        />
      )}

      <EmailCampaignUnpublishConfirmModal
        open={unpublishConfirmOpen}
        confirming={unpublishConfirming}
        scheduledType={state.campaign.scheduled_type}
        onClose={() => setUnpublishConfirmOpen(false)}
        onConfirm={() => {
          void confirmUnpublish();
        }}
      />
    </Page>
  );
}
